import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { keyframes } from '@mui/system';

// Keyframe animations
const spotlightPulse = keyframes`
  0%, 100% { opacity: 0.4; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.7; transform: translate(-50%, -50%) scale(1.1); }
`;

const spotlightSweep = keyframes`
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
`;

const textGlow = keyframes`
  0%, 100% {
    text-shadow: 0 0 20px rgba(135, 206, 235, 0.5), 0 0 40px rgba(135, 206, 235, 0.3), 0 0 60px rgba(135, 206, 235, 0.2);
    filter: brightness(1);
  }
  50% {
    text-shadow: 0 0 40px rgba(135, 206, 235, 0.8), 0 0 80px rgba(135, 206, 235, 0.5), 0 0 120px rgba(135, 206, 235, 0.3);
    filter: brightness(1.2);
  }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0); }
  50% { opacity: 1; transform: scale(1); }
`;

const curtainWave = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

// Particle component for magical effect
const Particle = ({ delay, left, duration }) => (
  <Box
    sx={{
      position: 'absolute',
      width: '4px',
      height: '4px',
      background: 'radial-gradient(circle, #87CEEB 0%, transparent 70%)',
      borderRadius: '50%',
      left: `${left}%`,
      bottom: '-10px',
      animation: `${keyframes`
        0% { transform: translateY(0) scale(1); opacity: 1; }
        100% { transform: translateY(-100vh) scale(0); opacity: 0; }
      `} ${duration}s ease-out ${delay}s infinite`,
      boxShadow: '0 0 10px #87CEEB, 0 0 20px #87CEEB',
    }}
  />
);

// Sparkle component
const Sparkle = ({ top, left, delay }) => (
  <Box
    sx={{
      position: 'absolute',
      width: '8px',
      height: '8px',
      top: `${top}%`,
      left: `${left}%`,
      animation: `${sparkle} 2s ease-in-out ${delay}s infinite`,
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        background: '#87CEEB',
      },
      '&::before': {
        width: '8px',
        height: '2px',
        top: '3px',
        left: '0',
      },
      '&::after': {
        width: '2px',
        height: '8px',
        top: '0',
        left: '3px',
      },
    }}
  />
);

const CurtainRaiser = ({ onComplete }) => {
  const [isRevealing, setIsRevealing] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [stage, setStage] = useState(0); // 0: initial, 1: text appears, 2: fully loaded
  const audioRef = useRef(null);

  useEffect(() => {
    // Staged appearance for dramatic effect
    const timer1 = setTimeout(() => setStage(1), 300);
    const timer2 = setTimeout(() => {
      setShowContent(true);
      setStage(2);
    }, 800);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const handleReveal = () => {
    setIsRevealing(true);

    // Complete after animation
    setTimeout(() => {
      localStorage.setItem('curtainShown', 'true');
      onComplete();
    }, 2200);
  };

  // Generate particles
  const particles = Array.from({ length: 30 }, (_, i) => ({
    delay: Math.random() * 5,
    left: Math.random() * 100,
    duration: 3 + Math.random() * 4,
  }));

  // Generate sparkles
  const sparkles = Array.from({ length: 15 }, (_, i) => ({
    top: 20 + Math.random() * 60,
    left: 10 + Math.random() * 80,
    delay: Math.random() * 3,
  }));

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 99999,
        overflow: 'hidden',
        background: 'radial-gradient(ellipse at center, #1a0a0a 0%, #0a0505 50%, #000000 100%)',
      }}
    >
      {/* Animated background stars */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `
            radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,0.3), transparent),
            radial-gradient(2px 2px at 40px 70px, rgba(255,255,255,0.2), transparent),
            radial-gradient(1px 1px at 90px 40px, rgba(255,255,255,0.4), transparent),
            radial-gradient(2px 2px at 130px 80px, rgba(255,255,255,0.2), transparent),
            radial-gradient(1px 1px at 160px 120px, rgba(255,255,255,0.3), transparent)
          `,
          backgroundSize: '200px 200px',
          opacity: 0.5,
          zIndex: 1,
        }}
      />

      {/* Floating particles */}
      <Box sx={{ position: 'absolute', width: '100%', height: '100%', zIndex: 2, pointerEvents: 'none' }}>
        {particles.map((p, i) => (
          <Particle key={i} {...p} />
        ))}
      </Box>

      {/* Sparkles */}
      <Box sx={{ position: 'absolute', width: '100%', height: '100%', zIndex: 25, pointerEvents: 'none' }}>
        {showContent && sparkles.map((s, i) => (
          <Sparkle key={i} {...s} />
        ))}
      </Box>

      {/* Left Curtain - Rich velvet with gold trim */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '52%',
          height: '100%',
          background: `
            linear-gradient(95deg,
              #4a0000 0%,
              #8B0000 8%,
              #DC143C 15%,
              #B22222 25%,
              #8B0000 35%,
              #DC143C 45%,
              #B22222 55%,
              #8B0000 65%,
              #DC143C 75%,
              #B22222 85%,
              #8B0000 92%,
              #4a0000 100%
            )
          `,
          backgroundSize: '200% 100%',
          animation: isRevealing ? 'none' : `${curtainWave} 8s ease-in-out infinite`,
          boxShadow: `
            inset -30px 0 60px rgba(0,0,0,0.6),
            inset 15px 0 30px rgba(255,200,150,0.1),
            10px 0 40px rgba(0,0,0,0.8)
          `,
          transform: isRevealing ? 'translateX(-100%) skewX(3deg)' : 'translateX(0)',
          transition: 'transform 1.8s cubic-bezier(0.77, 0, 0.175, 1)',
          zIndex: 10,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `
              repeating-linear-gradient(
                90deg,
                transparent 0px,
                transparent 25px,
                rgba(0,0,0,0.15) 25px,
                rgba(0,0,0,0.15) 28px,
                transparent 28px,
                transparent 50px
              )
            `,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: -5,
            width: '12px',
            height: '100%',
            background: 'linear-gradient(90deg, #87CEEB, #5DADE2, #87CEEB)',
            boxShadow: '0 0 20px rgba(135, 206, 235, 0.5)',
          }
        }}
      />

      {/* Right Curtain */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '52%',
          height: '100%',
          background: `
            linear-gradient(85deg,
              #4a0000 0%,
              #8B0000 8%,
              #DC143C 15%,
              #B22222 25%,
              #8B0000 35%,
              #DC143C 45%,
              #B22222 55%,
              #8B0000 65%,
              #DC143C 75%,
              #B22222 85%,
              #8B0000 92%,
              #4a0000 100%
            )
          `,
          backgroundSize: '200% 100%',
          animation: isRevealing ? 'none' : `${curtainWave} 8s ease-in-out infinite reverse`,
          boxShadow: `
            inset 30px 0 60px rgba(0,0,0,0.6),
            inset -15px 0 30px rgba(255,200,150,0.1),
            -10px 0 40px rgba(0,0,0,0.8)
          `,
          transform: isRevealing ? 'translateX(100%) skewX(-3deg)' : 'translateX(0)',
          transition: 'transform 1.8s cubic-bezier(0.77, 0, 0.175, 1)',
          zIndex: 10,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `
              repeating-linear-gradient(
                90deg,
                transparent 0px,
                transparent 25px,
                rgba(0,0,0,0.15) 25px,
                rgba(0,0,0,0.15) 28px,
                transparent 28px,
                transparent 50px
              )
            `,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: -5,
            width: '12px',
            height: '100%',
            background: 'linear-gradient(90deg, #87CEEB, #5DADE2, #87CEEB)',
            boxShadow: '0 0 20px rgba(135, 206, 235, 0.5)',
          }
        }}
      />

      {/* Top Valance - Ornate */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '-5%',
          width: '110%',
          height: '100px',
          background: 'linear-gradient(180deg, #3a0000 0%, #6B0000 40%, #8B0000 100%)',
          boxShadow: '0 15px 50px rgba(0,0,0,0.7)',
          zIndex: 15,
          transform: isRevealing ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 1.5s cubic-bezier(0.77, 0, 0.175, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '30px',
            background: 'linear-gradient(90deg, #87CEEB, #5DADE2, #87CEEB, #5DADE2, #87CEEB)',
            backgroundSize: '100px 100%',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -35,
            left: '5%',
            width: '90%',
            height: '50px',
            background: `
              radial-gradient(ellipse 40px 50px at 50px 0%, #8B0000 45%, transparent 50%),
              radial-gradient(ellipse 40px 50px at 150px 0%, #8B0000 45%, transparent 50%),
              radial-gradient(ellipse 40px 50px at 250px 0%, #8B0000 45%, transparent 50%),
              radial-gradient(ellipse 40px 50px at 350px 0%, #8B0000 45%, transparent 50%),
              radial-gradient(ellipse 40px 50px at 450px 0%, #8B0000 45%, transparent 50%),
              radial-gradient(ellipse 40px 50px at 550px 0%, #8B0000 45%, transparent 50%),
              radial-gradient(ellipse 40px 50px at 650px 0%, #8B0000 45%, transparent 50%),
              radial-gradient(ellipse 40px 50px at 750px 0%, #8B0000 45%, transparent 50%),
              radial-gradient(ellipse 40px 50px at 850px 0%, #8B0000 45%, transparent 50%),
              radial-gradient(ellipse 40px 50px at 950px 0%, #8B0000 45%, transparent 50%)
            `,
          }
        }}
      />

      {/* Multiple Spotlight Effects */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '800px',
          height: '800px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 25%, transparent 60%)',
          borderRadius: '50%',
          animation: `${spotlightPulse} 4s ease-in-out infinite`,
          zIndex: 5,
          pointerEvents: 'none',
        }}
      />

      {/* Secondary rotating spotlight */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '1000px',
          height: '1000px',
          background: `
            conic-gradient(
              from 0deg,
              transparent 0deg,
              rgba(135,206,235,0.03) 30deg,
              transparent 60deg,
              transparent 120deg,
              rgba(135,206,235,0.03) 150deg,
              transparent 180deg,
              transparent 240deg,
              rgba(135,206,235,0.03) 270deg,
              transparent 300deg
            )
          `,
          borderRadius: '50%',
          animation: `${spotlightSweep} 20s linear infinite`,
          zIndex: 4,
          pointerEvents: 'none',
        }}
      />

      {/* Center Content */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          zIndex: 20,
          opacity: showContent ? 1 : 0,
          transition: 'opacity 1s ease-out',
        }}
      >
        {/* Decorative top flourish */}
        <Box
          sx={{
            width: '120px',
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #FFD700, transparent)',
            margin: '0 auto 20px',
            opacity: isRevealing ? 0 : 1,
            transition: 'opacity 0.3s',
            animation: showContent ? `${fadeInScale} 0.8s ease-out` : 'none',
          }}
        />

        {/* Introducing Text */}
        <Typography
          sx={{
            color: '#87CEEB',
            fontSize: { xs: '0.9rem', md: '1.2rem' },
            fontWeight: 300,
            letterSpacing: '12px',
            textTransform: 'uppercase',
            mb: 2,
            animation: showContent ? `${fadeInUp} 0.8s ease-out` : 'none',
            opacity: isRevealing ? 0 : 1,
            transition: 'opacity 0.4s ease-out',
            fontFamily: '"Cormorant Garamond", Georgia, serif',
          }}
        >
          Introducing
        </Typography>

        {/* MillMind Logo/Text */}
        <Typography
          sx={{
            color: '#FFFFFF',
            fontSize: { xs: '3.5rem', sm: '5rem', md: '7rem' },
            fontWeight: 800,
            letterSpacing: '6px',
            mb: 1,
            lineHeight: 1,
            animation: showContent ? `${fadeInUp} 0.8s ease-out 0.15s both, ${textGlow} 3s ease-in-out infinite` : 'none',
            opacity: isRevealing ? 0 : 1,
            transition: 'opacity 0.4s ease-out',
            fontFamily: '"Playfair Display", Georgia, serif',
          }}
        >
          MillMind<sup style={{ fontSize: '0.3em', verticalAlign: 'super', marginLeft: '2px' }}>™</sup>
        </Typography>

        {/* Tagline */}
        <Typography
          component="span"
          sx={{
            display: 'block',
            color: '#87CEEB',
            fontSize: { xs: '1.2rem', md: '1.5rem' },
            fontWeight: 400,
            letterSpacing: '6px',
            mb: 3,
            animation: showContent ? `${fadeInUp} 0.8s ease-out 0.3s both` : 'none',
            opacity: isRevealing ? 0 : 1,
            transition: 'opacity 0.4s ease-out',
          }}
        >
          AI-Powered Mill Intelligence
        </Typography>

        {/* Decorative divider */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mb: 4,
            opacity: isRevealing ? 0 : 1,
            transition: 'opacity 0.4s',
            animation: showContent ? `${fadeInScale} 0.8s ease-out 0.4s both` : 'none',
          }}
        >
          <Box sx={{ width: '60px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.6))' }} />
          <Box sx={{ width: '8px', height: '8px', background: '#FFD700', transform: 'rotate(45deg)', boxShadow: '0 0 10px rgba(255,215,0,0.5)' }} />
          <Box sx={{ width: '60px', height: '1px', background: 'linear-gradient(90deg, rgba(255,215,0,0.6), transparent)' }} />
        </Box>

        {/* Reveal Button */}
        <Button
          onClick={handleReveal}
          disabled={isRevealing}
          sx={{
            px: 6,
            py: 2,
            fontSize: { xs: '0.9rem', md: '1.1rem' },
            fontWeight: 600,
            letterSpacing: '3px',
            textTransform: 'uppercase',
            color: '#0a0a0a',
            background: 'linear-gradient(135deg, #87CEEB 0%, #5DADE2 25%, #87CEEB 50%, #5DADE2 75%, #87CEEB 100%)',
            backgroundSize: '300% 100%',
            border: '2px solid rgba(135,206,235,0.3)',
            borderRadius: '4px',
            cursor: 'pointer',
            animation: showContent ? `${fadeInUp} 0.8s ease-out 0.5s both, ${shimmer} 4s linear infinite, ${float} 3s ease-in-out infinite` : 'none',
            opacity: isRevealing ? 0 : 1,
            transition: 'all 0.4s ease-out',
            boxShadow: '0 4px 30px rgba(135, 206, 235, 0.4), 0 0 60px rgba(135, 206, 235, 0.2)',
            '&:hover': {
              transform: 'scale(1.08) translateY(-5px)',
              boxShadow: '0 8px 50px rgba(135, 206, 235, 0.6), 0 0 80px rgba(135, 206, 235, 0.3)',
            },
            '&:disabled': {
              opacity: 0,
            }
          }}
        >
          Enter the Experience
        </Button>

        {/* Paperex Badge */}
        <Box
          sx={{
            mt: 5,
            animation: showContent ? `${fadeInUp} 0.8s ease-out 0.7s both` : 'none',
            opacity: isRevealing ? 0 : 1,
            transition: 'opacity 0.4s ease-out',
          }}
        >
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: '0.75rem',
              letterSpacing: '4px',
              textTransform: 'uppercase',
            }}
          >
            World Premiere
          </Typography>
          <Typography
            sx={{
              color: 'rgba(135,206,235,0.9)',
              fontSize: '0.9rem',
              fontWeight: 600,
              letterSpacing: '3px',
              mt: 0.5,
            }}
          >
            PAPEREX 2025
          </Typography>
        </Box>
      </Box>

      {/* Stage floor with reflection */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '200px',
          background: `
            linear-gradient(0deg,
              rgba(20,10,5,0.95) 0%,
              rgba(40,20,10,0.7) 30%,
              transparent 100%
            )
          `,
          zIndex: 3,
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '10%',
            width: '80%',
            height: '2px',
            background: 'linear-gradient(90deg, transparent, rgba(135,206,235,0.3), transparent)',
          }
        }}
      />

      {/* Vignette effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)',
          zIndex: 6,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default CurtainRaiser;
